/**
 * 前端埋点事件的属性(《埋点与统计口径》§2,与 @cg/shared 登记同名同形):各页面经这里拼好属性再 `track`。
 * 契约测试(client-events.test.ts)把每一个都过一遍登记校验 —— 属性名写错、少字段、类型不对,测试里就报出来,
 * 不用等上线后被服务端拒收计数。新加前端事件:先在 @cg/shared 登记,再在这里加一项。
 */
export interface ClientEvent {
  name: string;
  props: Record<string, unknown>;
}

export const clientEvents = {
  /** 注册表单打开(来源:推广链接 / 平台 / 机构站点) */
  registerStarted: (source: 'link' | 'platform' | 'org_site'): ClientEvent => ({
    name: 'account.register.started',
    props: { source },
  }),
  /** 大赛官网页面浏览(模块按路由归类;外站来源只留域名) */
  sitePageViewed: (contestId: string, module: string, referrer?: string): ClientEvent => ({
    name: 'contest.site.page_viewed',
    props: { contest_id: contestId, module, ...(referrer ? { referrer } : {}) },
  }),
  /** 推广链接落地页 */
  linkClicked: (channelId: string, linkId: string): ClientEvent => ({
    name: 'channel.link.clicked',
    props: { channel_id: channelId, link_id: linkId },
  }),
  /**
   * 招生页面打开(机构站点首页 / 家长说明页 / 预约页 / 活动页):招生漏斗的「访问」。
   * source = 从哪来的(官网 site · 推广链接 link · 海报 poster · 老带新 share);记在机构站点上
   */
  enrollPageViewed: (
    page: 'home' | 'intro' | 'booking' | 'activity',
    source: string,
    activityId?: string,
  ): ClientEvent => ({
    name: 'enroll.page.viewed',
    props: { page, source: source || 'site', ...(activityId ? { activity_id: activityId } : {}) },
  }),
  /** 讲解播放每过 25% 一次 */
  lectureProgress: (levelId: string, percent: 25 | 50 | 75 | 100): ClientEvent => ({
    name: 'learn.lecture.progress',
    props: { level_id: levelId, percent },
  }),
  /** 结算页生成分享图 */
  shareGenerated: (levelId: string): ClientEvent => ({
    name: 'learn.share.generated',
    props: { level_id: levelId },
  }),
  /** 成长地图:看到几条产品线、已开通几条 */
  journeyViewed: (visibleLines: number, openedLines: number): ClientEvent => ({
    name: 'learn.journey.viewed',
    props: { visible_lines: visibleLines, opened_lines: openedLines },
  }),
  /** 错题本 / 知识点分析跳到对应关卡 */
  knowledgeJumped: (knowledgePoint: string, levelId: string, unlocked: boolean): ClientEvent => ({
    name: 'drill.knowledge.jumped',
    props: { knowledge_point: knowledgePoint, level_id: levelId, unlocked },
  }),
  /** 检录 / 考试中共享屏幕开始与被停 */
  screenShare: (recordId: string, state: 'started' | 'stopped'): ClientEvent => ({
    name: 'exam.checkin.screen_share',
    props: { record_id: recordId, state },
  }),
  /** 首屏最大内容绘制(LCP) */
  pageLoad: (lcpMs: number, page: string): ClientEvent => ({
    name: 'sys.page.load',
    props: { lcp_ms: Math.min(Math.max(0, lcpMs), 120_000), page },
  }),
  /** 页面报错(只报消息指纹) */
  pageError: (messageHash: string, page: string): ClientEvent => ({
    name: 'sys.page.error',
    props: { message_hash: messageHash, page },
  }),
  /** 学生端 H5:手机上碰到要用电脑才能做的事(考试答题 / 检录、阶段测评、编程题、作品提交);page = 路由名 */
  desktopRequired: (
    what: 'exam' | 'checkin' | 'assessment' | 'coding' | 'work',
    page: string,
  ): ClientEvent => ({
    name: 'sys.h5.desktop_required',
    props: { what, page },
  }),
};
